import React, { useState, useMemo } from "react";
import { Drawer } from "@mui/material";
import { Checkbox } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import styles from "./CustomiseColumns.module.css";
import CustomButton from "../../components/buttons/CustomButton";
import cross from "./../../../assets/images/refresh-cw 1.svg";
import UpArrow from "./../../../assets/images/Uparrow.svg";
import DownArrow from "./../../../assets/images/Vector-1.svg";
import SnackBarMessage from "../snackbar/snackbar";
import { saveColumnPreference } from "Samarth Module/api/api";
import { smartProdSchedularInputFields } from "Samarth Module/constants/dailyPhasing";

function CustomiseColumns({
    anchor,
    isOpen,
    columns,
    defaultColumns = [],
    columnCustomization,
    handleCustomizeDrawer,
    username,
    type,
    /** When false, Apply updates local state only (no /api/saveColumnPreferences). */
    persistToServer = true,
}) {
    const normalizeLabel = (value) =>
        String(value || "")
            .replace(/\s+/g, "")
            .replace(/_/g, "")
            .toLowerCase();

    const isSubColumn = (label) => typeof label === "string" && label.startsWith("Sub_");

    const getSubColumnMeta = (label) => {
        if (!isSubColumn(label)) {
            return null;
        }

        const raw = label.slice(4);
        const separatorIndex = raw.lastIndexOf("_");

        if (separatorIndex < 0) {
            return null;
        }

        return {
            parentLabel: raw.slice(0, separatorIndex).trim(),
            childLabel: raw.slice(separatorIndex + 1).trim(),
        };
    };

    const getSubColumnParentLabel = (label) => getSubColumnMeta(label)?.parentLabel || "";

    const getSubColumnChildLabel = (label) => getSubColumnMeta(label)?.childLabel || label;

    const getParentChildCount = (columnList, parentLabel) =>
        (columnList || []).filter(
            (col) => isSubColumn(col?.label) && isParentOfSubColumn(parentLabel, col?.label)
        ).length;

    const isParentOfSubColumn = (parentLabel, subLabel) => {
        const subParentLabel = getSubColumnParentLabel(subLabel);
        return normalizeLabel(parentLabel) === normalizeLabel(subParentLabel);
    };

    const groupSubColumnsUnderParent = (sortedList) => {
        const parents = sortedList.filter((col) => !isSubColumn(col?.label));
        const children = sortedList.filter((col) => isSubColumn(col?.label));
        const consumedChildren = new Set();
        const result = [];

        parents.forEach((parent) => {
            result.push(parent);
            const matchingChildren = children.filter((child) => isParentOfSubColumn(parent?.label, child?.label));
            matchingChildren.forEach((child) => {
                consumedChildren.add(child?.label);
                result.push(child);
            });
        });

        // Keep unmatched sub columns visible at the end instead of dropping them.
        children.forEach((child) => {
            if (!consumedChildren.has(child?.label)) {
                result.push(child);
            }
        });

        return result;
    };

    // Sort columns by DisplayOrder when initializing
    const sortColumnsByDisplayOrder = (columnsData) => {
        if (Array.isArray(columnsData)) {
            const sortedList = [...columnsData].sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));
            return groupSubColumnsUnderParent(sortedList);
        } else {
            // For multiple tables
            const sortedColumns = {};
            Object.keys(columnsData).forEach(tblName => {
                const sortedList = [...columnsData[tblName]].sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0));
                sortedColumns[tblName] = groupSubColumnsUnderParent(sortedList);
            });
            return sortedColumns;
        }
    };

    const [localColumnsDetails, setLocalColumnsDetails] = useState(sortColumnsByDisplayOrder(columns));
    const [isSaving, setIsSaving] = useState(false);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: ' ',
        type: 'error'
    });

    const getColumnCheckboxState = (columnList, column) => {
        if (!Array.isArray(columnList) || !column?.label) {
            return { checked: !!column?.visibility, indeterminate: false };
        }

        if (isSubColumn(column.label)) {
            return { checked: !!column.visibility, indeterminate: false };
        }

        const childColumns = columnList.filter(
            (col) => isSubColumn(col?.label) && isParentOfSubColumn(column?.label, col?.label)
        );

        if (!childColumns.length) {
            return { checked: !!column.visibility, indeterminate: false };
        }

        const allSelected = childColumns.every((col) => !!col.visibility);
        const someSelected = childColumns.some((col) => !!col.visibility);

        return {
            checked: allSelected,
            indeterminate: someSelected && !allSelected,
        };
    };

    const MAI_ALLOWED_COLUMNS = useMemo(
        () => new Set(["Site code", "Site cluster", "MAI % (Physical)", "MAI % (Total)", "Material Id", "Material description"]),
        []
    );

    const MAI_SITE_DEPENDENT_COLUMNS = useMemo(
        () => new Set(["Lead Time(Min-Max)", "Inter-unit transfer view", "Stock VS Norm"]),
        []
    );

    const isMaiMaterialColumnsHidden = (columnList) => {
        if (type !== "MAI" || !Array.isArray(columnList)) {
            return false;
        }

        const materialIdColumn = columnList.find(
            (col) => !isSubColumn(col?.label) && normalizeLabel(col?.label) === normalizeLabel("Material Id")
        );
        const materialDescriptionColumn = columnList.find(
            (col) => !isSubColumn(col?.label) && normalizeLabel(col?.label) === normalizeLabel("Material description")
        );

        return materialIdColumn?.visibility === false && materialDescriptionColumn?.visibility === false;
    };

    const isAnyMaiSiteColumnHidden = (columnList) => {
        if (type !== "MAI" || !Array.isArray(columnList)) {
            return false;
        }

        const siteCodeColumn = columnList.find(
            (col) => !isSubColumn(col?.label) && normalizeLabel(col?.label) === normalizeLabel("Site code")
        );
        const siteClusterColumn = columnList.find(
            (col) => !isSubColumn(col?.label) && normalizeLabel(col?.label) === normalizeLabel("Site cluster")
        );

        return siteCodeColumn?.visibility === false;
    };

    const isColumnDisabledByMaiRule = (columnList, col) => {
        if (type !== "MAI") {
            return false;
        }

        if (!col?.label) {
            return false;
        }

        const parentLabel = isSubColumn(col.label) ? getSubColumnParentLabel(col.label) : col.label;

        if (isMaiMaterialColumnsHidden(columnList)) {
            return !MAI_ALLOWED_COLUMNS.has(parentLabel);
        }

        if (isAnyMaiSiteColumnHidden(columnList)) {
            return MAI_SITE_DEPENDENT_COLUMNS.has(parentLabel);
        }

        return false;
    };

    // Define restricted columns for MAI type
    // const restrictedColumns = [
        // "CBU Count",
        // "Stock Cover (Days)",
        // "Physical Stock (Qty)",
        // "Physical Stock (INR)",
        // "Past Due PO",
        // "Future PO",
        // "Future Open PO",
        // "Demand",
        // "Stock (Physical)",
        // "MAI % (Physical)"
    // ];

    // Function to count selected columns
    const countSelectedColumns = (columnsList) => {
        console.log("Counting selected columns in:", columnsList);
        if (Array.isArray(columnsList)) {
            return columnsList.filter(
                (col) => col.visibility
            ).length;
        }
        console.log("column list length:", columnsList.filter(
                (col) => col.visibility
            ).length);
        return 0; // default if columnsList is not an array
    };

    const handleCheckedColumns = (_, index, tblName) => {
        const columnList = tblName ? [...localColumnsDetails[tblName]] : [...localColumnsDetails];
        const temp = { ...columnList[index] };

        // Check if trying to select more than 7 columns for MAI type (excluding restricted columns)
        //if (type === 'MAI') {
            // Only check the limit if we're trying to CHECK (select) a column, not uncheck it
          //  if (!temp.visibility) { // If column is currently unchecked and we're trying to check it
            //    const currentSelectedCount = countSelectedColumns(localColumnsDetails);
            //    console.log("Current selected count (excluding restricted columns):", currentSelectedCount);
                // if (currentSelectedCount >= 7) {
                //     setSnackBarObject({
                //         snackbarOpen: true,
                //         message: "You can't select more than 7 columns for MAI type",
                //         type: "error",
                //     });
            //        return;
            //    }
           // }
        //}
        
        const isParentColumn = !isSubColumn(temp?.label);
        if (isParentColumn) {
            const childIndices = columnList
                .map((col, idx) => (
                    isSubColumn(col?.label) && isParentOfSubColumn(temp?.label, col?.label) ? idx : -1
                ))
                .filter((idx) => idx > -1);

            if (childIndices.length) {
                const shouldEnable = childIndices.some((childIdx) => !columnList[childIdx]?.visibility);
                temp["visibility"] = shouldEnable;
                columnList.splice(index, 1, temp);

                childIndices.forEach((childIdx) => {
                    const childCol = { ...columnList[childIdx], visibility: shouldEnable };
                    columnList.splice(childIdx, 1, childCol);
                });
            } else {
                temp["visibility"] = !temp["visibility"];
                columnList.splice(index, 1, temp);
            }
        } else {
            temp["visibility"] = !temp["visibility"];
            columnList.splice(index, 1, temp);

            const parentLabel = getSubColumnParentLabel(temp.label);
            const parentIndex = columnList.findIndex(
                (col) => !isSubColumn(col?.label) && normalizeLabel(col?.label) === normalizeLabel(parentLabel)
            );

            if (parentIndex > -1) {
                const hasVisibleChild = columnList.some(
                    (col) => isSubColumn(col?.label) && normalizeLabel(getSubColumnParentLabel(col.label)) === normalizeLabel(parentLabel) && !!col.visibility
                );
                const parentCol = { ...columnList[parentIndex], visibility: hasVisibleChild };
                columnList.splice(parentIndex, 1, parentCol);
            }
        }
        
        // Sort and regroup by DisplayOrder to keep child rows nested under their parent.
        const sortedColumnList = sortColumnsByDisplayOrder(columnList);
        
        if (tblName) {
            setLocalColumnsDetails(prev => ({ ...prev, [tblName]: sortedColumnList }));
        } else {
            setLocalColumnsDetails(sortedColumnList);
        }
    };

    const closeDrawer = () => {
        setLocalColumnsDetails(sortColumnsByDisplayOrder(columns));
        handleCustomizeDrawer();
    };

    const getChildGroupBounds = (list, index) => {
        const current = list?.[index];
        if (!current || !isSubColumn(current?.label)) {
            return null;
        }

        const parentLabel = getSubColumnParentLabel(current.label);
        const siblingIndices = (list || [])
            .map((col, idx) => (
                isSubColumn(col?.label) && normalizeLabel(getSubColumnParentLabel(col.label)) === normalizeLabel(parentLabel)
                    ? idx
                    : -1
            ))
            .filter((idx) => idx > -1);

        if (!siblingIndices.length) {
            return null;
        }

        return {
            firstIndex: siblingIndices[0],
            lastIndex: siblingIndices[siblingIndices.length - 1],
        };
    };

    const getParentBlockBounds = (list, parentIndex) => {
        const parent = list?.[parentIndex];
        if (!parent || isSubColumn(parent?.label)) {
            return null;
        }

        let endIndex = parentIndex;
        while (
            endIndex + 1 < list.length &&
            isSubColumn(list[endIndex + 1]?.label) &&
            isParentOfSubColumn(parent.label, list[endIndex + 1].label)
        ) {
            endIndex += 1;
        }

        return { startIndex: parentIndex, endIndex };
    };

    const moveBlock = (list, startIndex, endIndex, targetStartIndex) => {
        const result = [...list];
        const block = result.splice(startIndex, endIndex - startIndex + 1);

        let insertAt = targetStartIndex;
        if (targetStartIndex > startIndex) {
            insertAt -= block.length;
        }

        result.splice(insertAt, 0, ...block);
        return result;
    };

    const getParentStartForIndex = (list, index) => {
        let cursor = Math.min(index, list.length - 1);
        while (cursor >= 0 && isSubColumn(list[cursor]?.label)) {
            cursor -= 1;
        }
        return cursor;
    };

    const moveUp = (index, tblName) => {
        if (index > 0) {
            const newColumns = tblName ? [...localColumnsDetails[tblName]] : [...localColumnsDetails];
            const currentColumn = newColumns[index];
            if (isSubColumn(currentColumn?.label)) {
                const bounds = getChildGroupBounds(newColumns, index);
                if (!bounds || index - 1 < bounds.firstIndex) {
                    return;
                }
                const [movedColumn] = newColumns.splice(index, 1);
                newColumns.splice(index - 1, 0, movedColumn);
            } else {
                const currentBounds = getParentBlockBounds(newColumns, index);
                if (!currentBounds) {
                    return;
                }

                const previousParentStart = getParentStartForIndex(newColumns, currentBounds.startIndex - 1);
                if (previousParentStart < 0) {
                    return;
                }

                const movedAsBlock = moveBlock(
                    newColumns,
                    currentBounds.startIndex,
                    currentBounds.endIndex,
                    previousParentStart
                );

                newColumns.splice(0, newColumns.length, ...movedAsBlock);
            }
            
            // Update DisplayOrder to reflect new position
            newColumns.forEach((col, idx) => {
                col.DisplayOrder = idx;
            });
            
            if (tblName) {
                setLocalColumnsDetails(prev => ({ ...prev, [tblName]: newColumns }));
            } else {
                setLocalColumnsDetails(newColumns);
            }
        }
    };

    const moveDown = (index, tblName) => {
        if (tblName) {
            if (index < localColumnsDetails[tblName].length - 1) {
                const newColumns = [...localColumnsDetails[tblName]];
                const currentColumn = newColumns[index];
                if (isSubColumn(currentColumn?.label)) {
                    const bounds = getChildGroupBounds(newColumns, index);
                    if (!bounds || index + 1 > bounds.lastIndex) {
                        return;
                    }
                    const [movedColumn] = newColumns.splice(index, 1);
                    newColumns.splice(index + 1, 0, movedColumn);
                } else {
                    const currentBounds = getParentBlockBounds(newColumns, index);
                    if (!currentBounds) {
                        return;
                    }

                    const nextParentStart = getParentStartForIndex(newColumns, currentBounds.endIndex + 1);
                    if (nextParentStart < 0) {
                        return;
                    }
                    const nextParentBounds = getParentBlockBounds(newColumns, nextParentStart);
                    if (!nextParentBounds) {
                        return;
                    }

                    const movedAsBlock = moveBlock(
                        newColumns,
                        currentBounds.startIndex,
                        currentBounds.endIndex,
                        nextParentBounds.endIndex + 1
                    );

                    newColumns.splice(0, newColumns.length, ...movedAsBlock);
                }
                
                // Update DisplayOrder to reflect new position
                newColumns.forEach((col, idx) => {
                    col.DisplayOrder = idx;
                });
                
                setLocalColumnsDetails(prev => ({ ...prev, [tblName]: newColumns }));
            }
        } else {
            if (index < localColumnsDetails.length - 1) {
                const newColumns = [...localColumnsDetails];
                const currentColumn = newColumns[index];
                if (isSubColumn(currentColumn?.label)) {
                    const bounds = getChildGroupBounds(newColumns, index);
                    if (!bounds || index + 1 > bounds.lastIndex) {
                        return;
                    }
                    const [movedColumn] = newColumns.splice(index, 1);
                    newColumns.splice(index + 1, 0, movedColumn);
                } else {
                    const currentBounds = getParentBlockBounds(newColumns, index);
                    if (!currentBounds) {
                        return;
                    }

                    const nextParentStart = getParentStartForIndex(newColumns, currentBounds.endIndex + 1);
                    if (nextParentStart < 0) {
                        return;
                    }
                    const nextParentBounds = getParentBlockBounds(newColumns, nextParentStart);
                    if (!nextParentBounds) {
                        return;
                    }

                    const movedAsBlock = moveBlock(
                        newColumns,
                        currentBounds.startIndex,
                        currentBounds.endIndex,
                        nextParentBounds.endIndex + 1
                    );

                    newColumns.splice(0, newColumns.length, ...movedAsBlock);
                }
                
                // Update DisplayOrder to reflect new position
                newColumns.forEach((col, idx) => {
                    col.DisplayOrder = idx;
                });
                
                setLocalColumnsDetails(newColumns);
            }
        }
    };

    const handleMoveUpClick = (event) => {
        const { index, tblname, disabled } = event.currentTarget.dataset;
        if (disabled === "true") {
            return;
        }

        const parsedIndex = Number(index);
        if (Number.isNaN(parsedIndex)) {
            return;
        }

        moveUp(parsedIndex, tblname || undefined);
    };

    const handleMoveDownClick = (event) => {
        const { index, tblname, disabled } = event.currentTarget.dataset;
        if (disabled === "true") {
            return;
        }

        const parsedIndex = Number(index);
        if (Number.isNaN(parsedIndex)) {
            return;
        }

        moveDown(parsedIndex, tblname || undefined);
    };

    const closeSnackbar = () => {
        setSnackBarObject({ message: '', snackbarOpen: false, type: 'error' });
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        
        // Update DisplayOrder to reflect new positions
        result.forEach((col, idx) => {
            col.DisplayOrder = idx;
        });
        
        return result;
    };

    const onDragEnd = (result, tblName) => {
        const { destination, source } = result;
        if (!destination) {
            return;
        }
        if (destination.index === source.index) {
            return;
        }

        const sourceList = Array.isArray(localColumnsDetails) ? localColumnsDetails : localColumnsDetails[tblName];
        const sourceColumn = sourceList?.[source.index];
        if (isSubColumn(sourceColumn?.label)) {
            const bounds = getChildGroupBounds(sourceList, source.index);
            if (!bounds || destination.index < bounds.firstIndex || destination.index > bounds.lastIndex) {
                return;
            }

            const reorderedList = reorder(
                sourceList,
                source.index,
                destination.index
            );

            if (tblName && typeof tblName === 'string') {
                setLocalColumnsDetails(prev => ({ ...prev, [tblName]: reorderedList }));
            } else {
                setLocalColumnsDetails(reorderedList);
            }
            return;
        }

        const sourceBlock = getParentBlockBounds(sourceList, source.index);
        if (!sourceBlock) {
            return;
        }

        const destinationParentStart = getParentStartForIndex(sourceList, destination.index);
        if (destinationParentStart < 0) {
            return;
        }
        const destinationBlock = getParentBlockBounds(sourceList, destinationParentStart);
        if (!destinationBlock) {
            return;
        }

        // If dropped within the same parent block, no effective parent movement.
        if (destinationParentStart === sourceBlock.startIndex) {
            return;
        }

        const targetStartIndex = sourceBlock.startIndex < destination.index
            ? destinationBlock.endIndex + 1
            : destinationBlock.startIndex;

        const reorderedList = moveBlock(
            sourceList,
            sourceBlock.startIndex,
            sourceBlock.endIndex,
            targetStartIndex
        );

        reorderedList.forEach((col, idx) => {
            col.DisplayOrder = idx;
        });
        
        if (tblName && typeof tblName === 'string') {
            setLocalColumnsDetails(prev => ({ ...prev, [tblName]: reorderedList }));
        } else {
            setLocalColumnsDetails(reorderedList);
        }
    };

    const checkingDefaultCols = (columnName, tblName) => {
        if (Array.isArray(defaultColumns)) {
            if (defaultColumns.includes(columnName.label) && (columnName.label != 'RMPM % Coverage' && columnName.label != 'Priority')) {
                return true;
            }
        } else {
            if (defaultColumns[tblName].includes(columnName.label)) {
                return true;
            }
        }
        return false;
    }

    const saveCustomiseColumn = async (filteredColumns) => {
        setIsSaving(true);
        try {
            let filteredCols;
            if (type === "MM Screen Input") {
                const derivedTbl2Cols = [...filteredColumns["Table 2"]].map((dict) => ({ ...dict, "label": dict["label"] + '_1' }))
                filteredCols = [...filteredColumns["Table 1"], ...derivedTbl2Cols]
            } else {
                filteredCols = filteredColumns.filter((col) => {
                    if (!col.label.includes('/')) {
                        if (type === "Daily Phasing" && col["label"] === "SAP Weekly Plan") {
                            return false;
                        }
                        return true;
                    } else {
                        if (type === "Daily Phasing" && col["label"] === "SAP Weekly Plan") {
                            return false;
                        } else if (type === "Daily Phasing" && col["label"] === "FG/SFG code") {
                            return true;
                        }
                        return false;
                    }
                });
            }
            if (!persistToServer) {
                columnCustomization(filteredColumns);
                setSnackBarObject({
                    snackbarOpen: true,
                    message: "Preferences applied successfully!",
                    type: "success",
                });
                return true;
            }
            const response = await saveColumnPreference(username, type, filteredCols);

            if (response && response.success) {
                columnCustomization(filteredColumns);
                setSnackBarObject({
                    snackbarOpen: true,
                    message: "Preferences saved successfully!",
                    type: "success",
                });
                return true;
            } else {
                throw new Error(response?.message || "Failed to save preferences");
            }
        } catch (error) {
            console.error('Save error:', error);
            setSnackBarObject({
                snackbarOpen: true,
                message: error.message || "Failed to save preferences",
                type: "error",
            });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    // Use useMemo to always sort columns by DisplayOrder before rendering
    const sortedColumns = useMemo(() => {
        return sortColumnsByDisplayOrder(localColumnsDetails);
    }, [localColumnsDetails]);

    const renderMultiTableDraggableItem = (tblName, tblColumns, col, idx) => {
        const checkboxState = getColumnCheckboxState(tblColumns, col);
        const isChildColumn = isSubColumn(col["label"]);
        const childCount = isChildColumn ? 0 : getParentChildCount(tblColumns, col?.label);
        const isDisabledByMaiRule = isColumnDisabledByMaiRule(tblColumns, col);
        const isInteractionDisabled = checkingDefaultCols(col, tblName) || isDisabledByMaiRule;

        return (
            <Draggable
                key={`${col.column}_${idx}`}
                draggableId={`${col.column}_${idx}`}
                index={idx}
                isDragDisabled={isInteractionDisabled}
            >
                {(provided) => (
                    <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={styles.listItem}
                    >
                        <div className={`${styles.columnList} ${isChildColumn ? styles.childColumnList : styles.parentColumnList} ${isDisabledByMaiRule ? styles.disabledColumnList : ''}`}>
                            <div className={styles.columnLabelWrap}>
                                <Checkbox
                                    size="small"
                                    checked={checkboxState.checked}
                                    indeterminate={checkboxState.indeterminate}
                                    onChange={(event) =>
                                        handleCheckedColumns(
                                            event.target.checked,
                                            idx,
                                            tblName
                                        )
                                    }
                                    disabled={isInteractionDisabled}
                                />
                                {isChildColumn && <span className={styles.treeConnector} />}
                                <span className={`${styles.columnText} ${isChildColumn ? styles.subColumnText : styles.parentColumnText} ${isDisabledByMaiRule ? styles.disabledColumnText : ''}`}>
                                    {isChildColumn ? getSubColumnChildLabel(col.label) : col.label}
                                </span>
                                {!isChildColumn && childCount > 0 && (
                                    <span className={styles.childrenCountBadge}>{childCount}</span>
                                )}
                            </div>
                            <div className={styles.listItems}>
                                <img
                                    src={UpArrow}
                                    onClick={handleMoveUpClick}
                                    data-index={idx}
                                    data-tblname={tblName}
                                    data-disabled={isInteractionDisabled || idx === 0}
                                    style={{
                                        cursor:
                                            isInteractionDisabled || idx === 0 ? "not-allowed" : "pointer",
                                        opacity: isInteractionDisabled ? 0.5 : 1,
                                    }}
                                    height={"10px"}
                                    width={"13px"}
                                    alt="upward arrow"
                                />
                                <img
                                    src={DownArrow}
                                    onClick={handleMoveDownClick}
                                    data-index={idx}
                                    data-tblname={tblName}
                                    data-disabled={isInteractionDisabled || idx === tblColumns.length - 1}
                                    style={{
                                        cursor:
                                            isInteractionDisabled || idx === tblColumns.length - 1
                                                ? "not-allowed"
                                                : "pointer",
                                        opacity: isInteractionDisabled ? 0.5 : 1,
                                    }}
                                    height={"10px"}
                                    width={"13px"}
                                    alt="downward arrow"
                                />
                            </div>
                        </div>
                    </li>
                )}
            </Draggable>
        );
    };

    const renderSingleTableDraggableItem = (col, idx) => {
        const checkboxState = getColumnCheckboxState(sortedColumns, col);
        const isChildColumn = isSubColumn(col["label"]);
        const childCount = isChildColumn ? 0 : getParentChildCount(sortedColumns, col?.label);
        const isDisabledByMaiRule = isColumnDisabledByMaiRule(sortedColumns, col);
        const isInteractionDisabled = checkingDefaultCols(col) || isDisabledByMaiRule;

        return (
            <Draggable
                key={`${col.column}_${idx}`}
                draggableId={`${col.column}_${idx}`}
                index={idx}
                isDragDisabled={isInteractionDisabled}
            >
                {(provided) => (
                    <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={styles.listItem}
                    >
                        <div className={`${styles.columnList} ${isChildColumn ? styles.childColumnList : styles.parentColumnList} ${isDisabledByMaiRule ? styles.disabledColumnList : ''}`}>
                            <div className={styles.columnLabelWrap}>
                                <Checkbox
                                    size="small"
                                    checked={checkboxState.checked}
                                    indeterminate={checkboxState.indeterminate}
                                    onChange={(event) =>
                                        handleCheckedColumns(event.target.checked, idx)
                                    }
                                    disabled={isInteractionDisabled}
                                />
                                {isChildColumn && <span className={styles.treeConnector} />}
                                <span className={`${styles.columnText} ${isChildColumn ? styles.subColumnText : styles.parentColumnText} ${isDisabledByMaiRule ? styles.disabledColumnText : ''}`}>
                                    {isChildColumn ? getSubColumnChildLabel(col.label) : col.label}
                                </span>
                                {!isChildColumn && childCount > 0 && (
                                    <span className={styles.childrenCountBadge}>{childCount}</span>
                                )}
                            </div>
                            <div className={styles.listItems}>
                                <img
                                    src={UpArrow}
                                    onClick={handleMoveUpClick}
                                    data-index={idx}
                                    data-disabled={isInteractionDisabled || idx === 0}
                                    style={{
                                        cursor: isInteractionDisabled || idx === 0 ? "not-allowed" : "pointer",
                                        opacity: isInteractionDisabled ? 0.5 : 1,
                                    }}
                                    height={"10px"}
                                    width={"13px"}
                                    alt="upward arrow"
                                />
                                <img
                                    src={DownArrow}
                                    onClick={handleMoveDownClick}
                                    data-index={idx}
                                    data-disabled={isInteractionDisabled || idx === sortedColumns.length - 1}
                                    style={{
                                        cursor:
                                            isInteractionDisabled || idx === sortedColumns.length - 1
                                                ? "not-allowed"
                                                : "pointer",
                                        opacity: isInteractionDisabled ? 0.5 : 1,
                                    }}
                                    height={"10px"}
                                    width={"13px"}
                                    alt="downward arrow"
                                />
                            </div>
                        </div>
                    </li>
                )}
            </Draggable>
        );
    };

    const shouldRenderColumn = (col) =>
        !smartProdSchedularInputFields.includes(col["label"]) &&
        !(type === "Daily Phasing" && col["label"] === "SAP Weekly Plan");

    return (
        <Drawer anchor={anchor} open={isOpen}>
            <div className={styles.drawerContainer}>
                {/* heading */}
                <div className={styles.heading}>
                    <p>Customise columns</p>
                    <img
                        src={cross}
                        className={styles.closeIcon}
                        onClick={closeDrawer}
                        alt="Close drawer"
                    />
                </div>
                {/* content */}
                <div className={styles.drawerContent}>
                    <div className={styles.contentText}>
                        Choose the columns you want to display in the table. To adjust the
                        order, use drag-and-drop or the 'up' and 'down' buttons next to each
                        column.
                    </div>
                    {Array.isArray(sortedColumns) ? (
                        // for only single table customization
                        <div className={styles.columnSelection}>
                            <p>Current selection</p>
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="droppable" direction="vertical">
                                    {(provided) => (
                                        <ul
                                            className={styles.list}
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {sortedColumns.map((col, idx) =>
                                                shouldRenderColumn(col)
                                                    ? renderSingleTableDraggableItem(col, idx)
                                                    : null
                                            )}
                                            {provided.placeholder}
                                        </ul>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    ) : (
                        // for multiple table customization(DP > mm input)
                        <>
                            {Object.keys(sortedColumns).map((tblName) => (
                                <div key={tblName} className={styles.columnSelection}>
                                    <p>Current selection - {tblName}</p>
                                    <DragDropContext onDragEnd={(result) => onDragEnd(result, tblName)}>
                                        <Droppable droppableId={`droppable-${tblName}`} direction="vertical">
                                            {(provided) => (
                                                <ul
                                                    className={styles.multipleTableList}
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                >
                                                    {sortedColumns[tblName].map((col, idx) =>
                                                        renderMultiTableDraggableItem(tblName, sortedColumns[tblName], col, idx)
                                                    )}
                                                    {provided.placeholder}
                                                </ul>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </div>
                            ))}
                        </>
                    )}

                </div>
                {/* buttons */}
                <div className={styles.drawerBtns}>
                    <CustomButton label="Cancel" type="mm-save" onClick={closeDrawer} />
                    <CustomButton label={isSaving ? "Saving..." : "Apply"} disabled={isSaving} type="mm-submit" onClick={() => saveCustomiseColumn(localColumnsDetails)} />
                </div>
                {snackBarObject["snackbarOpen"] && (
                    <SnackBarMessage
                        type={snackBarObject["type"]}
                        snackbarMessage={snackBarObject["message"]}
                        snackbarOpen={snackBarObject["snackbarOpen"]}
                        setSnackbarOpen={closeSnackbar}
                    />
                )}
            </div>
        </Drawer>
    );
}

export default CustomiseColumns;